import PropTypes from 'prop-types';
import {Label as PepsicoDSLabel, Tooltip} from '@pepsico-ds/ui';
import React from 'react';

/**
 * LabelWithTooltip component to display a label and corresponding data.
 *
 * @param {Object} props - The properties object.
 * @param {string|import('react').ReactNode} [props.label] - The label text or a custom node.
 * @param {string} [props.data] - The data/value to be displayed (backward compatibility).
 * @param {string} [props.id] - The unique identifier for the label element.
 * @param {boolean} [props.isDark] - Render in dark mode if true; uses PaletteContext otherwise.
 * @param {boolean} [props.disabled] - Mark the input as disabled.
 * @param {boolean} [props.required] - Mark the input as required.
 * @param {boolean} [props.showLabelIcon] - Render info icon next to label when true.
 * @param {Function} [props.onInfoClick] - Callback when info icon is clicked.
 * @param {string} [props.tooltipText] - The tooltip text to be displayed.
 * @param {string} [props.className] - The CSS class name for the label.
 * @param {Object} [props.style] - The style object containing direct styles to the component.
 * @returns {JSX.Element} The rendered Label component.
 */
const LabelWithTooltip = ({
  id,
  label,
  className,
  style,
  isDark,
  disabled,
  required,
  showLabelIcon,
  tooltipText,
  onInfoClick,
  ...rest
}) => {
  return (
    <div>
      <Tooltip direction='right' text={tooltipText} isDark={isDark}>
        <PepsicoDSLabel
          id={id}
          label={
            <div className={className} style={style}>
              {label}
            </div>
          }
          isDark={isDark}
          disabled={disabled}
          required={required}
          showLabelIcon={showLabelIcon}
          onInfoClick={onInfoClick}
          {...rest}
        />
      </Tooltip>
    </div>
  );
};

LabelWithTooltip.propTypes = {
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  data: PropTypes.string,
  isDark: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  showLabelIcon: PropTypes.bool,
  onInfoClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  tooltipText: PropTypes.string
};

export default React.memo(LabelWithTooltip);
